import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import type { QuitPage } from '@/features/webinar/types';
import { useWebinarT } from '@/hooks/useWebinarT';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { useAppDispatch } from '@/app/hooks';
import { postAnalyticsEvent } from '@/features/live/liveThunks';

interface QuitCardProps {
  quitPage: QuitPage;
  onSubmit?: (selectedReasonIds: number[], otherReasonText?: string) => void;
  onCancel?: () => void;
  visualIdentity?: WorkspaceVisualIdentity;
  webinarId?: number;
  sessionIdentifier?: string;
  authorization?: string;
}

export default function QuitCard({
  quitPage,
  onSubmit,
  onCancel,
  visualIdentity,
  webinarId,
  sessionIdentifier,
  authorization,
}: QuitCardProps) {
  const { t } = useWebinarT('createWebinar');
  const dispatch = useAppDispatch();
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [otherReasonSelected, setOtherReasonSelected] = useState<boolean>(false);
  const [otherReasonText, setOtherReasonText] = useState<string>('');
  const sortedReasons = [...(quitPage?.reasons ?? [])].sort(
    (a, b) => (a?.position ?? 0) - (b?.position ?? 0),
  );

  if (!quitPage || !Array.isArray(quitPage.reasons)) {
    return null;
  }
  const theme = visualIdentity?.theme || 'LIGHT';
  const baseTextColor = theme === 'DARK' ? '#FFFFFF' : '#000000';
  const baseSecondaryTextColor = theme === 'DARK' ? '#9E9FA6' : '#6D6E7A';

  // Couleurs personnalisables de l'exit pop-up : si le client n'a rien choisi
  // (valeur nulle), on retombe sur le rendu thème/accent actuel pour ne rien casser.
  const textColor = quitPage.textColor || baseTextColor;
  const secondaryTextColor = quitPage.textColor || baseSecondaryTextColor;
  const backgroundColor = quitPage.backgroundColor || visualIdentity?.backgroundColor || '#FFFFFF';
  const buttonColor = quitPage.buttonColor || visualIdentity?.accentColor || '#2c6cff';
  const buttonTextColor = quitPage.buttonTextColor || undefined;

  const liveQrUrl =
    typeof window !== 'undefined' && webinarId && sessionIdentifier && authorization
      ? `${window.location.origin}/live/${webinarId}/${sessionIdentifier}?token=${authorization}`
      : null;

  const handleOptionSelect = (index: number) => {
    if (quitPage.type === 'SINGLE_CHOICE') {
      setSelectedOptions([index]);
      setOtherReasonSelected(false);
    } else {
      setSelectedOptions((prev) =>
        prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
      );
    }
  };

  const handleSubmit = () => {
    if (webinarId && sessionIdentifier && authorization) {
      dispatch(
        postAnalyticsEvent({
          webinarId,
          sessionIdentifier,
          authorization,
          input: {
            eventType: 'LEFT',
            payloadJson: JSON.stringify({}),
            occurredAt: new Date().toISOString(),
          },
        }),
      );
    }

    if (quitPage.type === 'SINGLE_CHOICE') {
      const selectedIndex = selectedOptions[0];
      if (selectedIndex !== undefined) {
        const reasonId = sortedReasons[selectedIndex]?.id;
        if (reasonId) {
          onSubmit?.([reasonId], otherReasonSelected ? otherReasonText : undefined);
        }
      } else if (otherReasonSelected) {
        onSubmit?.([], otherReasonText);
      }
    } else {
      const selectedReasonIds = selectedOptions
        .map((index) => sortedReasons[index]?.id)
        .filter((id): id is number => id !== undefined);
      onSubmit?.(selectedReasonIds, otherReasonSelected ? otherReasonText : undefined);
    }
  };

  const canSubmit =
    selectedOptions.length > 0 ||
    (quitPage.otherReason && otherReasonSelected && otherReasonText.trim());

  return (
    <div
      className="flex flex-col gap-6  rounded-lg p-4 w-full lg:w-[600px] mx-auto"
      style={{
        backgroundColor,
      }}
    >
      <h1
        className="text-[32px]/[40px] font-semibold"
        style={{
          color: textColor,
          fontFamily: visualIdentity?.fontFamily || 'Poppins',
        }}
      >
        {quitPage.title}
      </h1>
      <p
        className="text-sm"
        style={{
          color: secondaryTextColor,
          fontFamily: visualIdentity?.fontFamily || 'Poppins',
        }}
      >
        {quitPage.questionText}
      </p>
      <div className="flex flex-col gap-4">
        {quitPage.type === 'SINGLE_CHOICE' ? (
          <RadioGroup
            value={selectedOptions.length > 0 ? `option-${selectedOptions[0]}` : ''}
            onValueChange={(value) => {
              if (value) {
                const index = parseInt(value.replace('option-', ''), 10);
                if (!isNaN(index)) {
                  setSelectedOptions([index]);
                }
              }
            }}
          >
            {sortedReasons.map((reason, index) => (
              <div key={reason.id} className="flex flex-col gap-4">
                <div
                  className={`flex items-center gap-2 p-2 border border-input rounded-lg cursor-pointer ${
                    selectedOptions.includes(index) ? '' : ''
                  }`}
                  onClick={() => handleOptionSelect(index)}
                >
                  <RadioGroupItem
                    value={`option-${index}`}
                    id={`option-${index}`}
                    checked={selectedOptions.includes(index)}
                    style={{
                      borderColor: visualIdentity?.secondaryBackgroundColor || '#E5E7EB',
                    }}
                  />
                  <label
                    htmlFor={`option-${index}`}
                    className="cursor-pointer flex-1"
                    style={{
                      color: textColor,
                      fontFamily: visualIdentity?.fontFamily || 'Poppins',
                    }}
                  >
                    {reason?.reason ?? ''}
                  </label>
                </div>
                {selectedOptions.includes(index) && (
                  <>
                    {reason?.explanationText && (
                      <p
                        className="text-sm"
                        style={{
                          color: secondaryTextColor,
                          fontFamily: visualIdentity?.fontFamily || 'Poppins',
                        }}
                      >
                        {reason?.explanationText ?? ''}
                      </p>
                    )}
                    {reason?.displayQrCode && liveQrUrl && (
                      <div className="flex items-center justify-center">
                        <QRCodeSVG value={liveQrUrl} size={248} className="" />
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            {quitPage.otherReason && (
              <div className="flex flex-col gap-4">
                <div
                  className={`flex items-center gap-2 p-2 border border-input rounded-lg cursor-pointer  ${
                    otherReasonSelected ? '' : ''
                  }`}
                  onClick={() => {
                    setOtherReasonSelected(true);
                    setSelectedOptions([]);
                  }}
                >
                  <RadioGroupItem
                    value="other-reason"
                    id="other-reason"
                    checked={otherReasonSelected}
                    style={{
                      borderColor: visualIdentity?.secondaryBackgroundColor || '#E5E7EB',
                    }}
                  />
                  <label
                    htmlFor="other-reason"
                    className="cursor-pointer flex-1"
                    style={{
                      color: textColor,
                      fontFamily: visualIdentity?.fontFamily || 'Poppins',
                    }}
                  >
                    {t('quitPopup.otherReason')}
                  </label>
                </div>
                {otherReasonSelected && (
                  <div>
                    <textarea
                      placeholder={t('quitPopup.otherReasonPlaceholder')}
                      value={otherReasonText}
                      onChange={(e) => setOtherReasonText(e.target.value)}
                      className="w-full p-3 border border-input rounded-lg resize-none"
                      rows={3}
                      style={{
                        color: textColor,
                        // backgroundColor: theme === 'DARK' ? 'rgba(255, 255, 255, 0.1)' : '#FFFFFF',
                        fontFamily: visualIdentity?.fontFamily || 'Poppins',
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </RadioGroup>
        ) : (
          <>
            {sortedReasons.map((reason, index) => (
              <div key={reason.id} className="flex flex-col gap-4">
                <div
                  className={`flex items-center gap-2 p-2 border border-input rounded-lg cursor-pointer  ${
                    selectedOptions.includes(index) ? '' : ''
                  }`}
                  onClick={() => handleOptionSelect(index)}
                >
                  <Checkbox
                    id={`checkbox-${index}`}
                    checked={selectedOptions.includes(index)}
                    onCheckedChange={() => handleOptionSelect(index)}
                    style={{
                      borderColor: visualIdentity?.secondaryBackgroundColor || undefined,
                    }}
                  />
                  <label
                    htmlFor={`checkbox-${index}`}
                    className="cursor-pointer flex-1"
                    style={{
                      color: textColor,
                      fontFamily: visualIdentity?.fontFamily || 'Poppins',
                    }}
                  >
                    {reason?.reason ?? ''}
                  </label>
                </div>
                {selectedOptions.includes(index) && (
                  <>
                    {reason?.explanationText && (
                      <p
                        className="text-sm"
                        style={{
                          color: secondaryTextColor,
                          fontFamily: visualIdentity?.fontFamily || 'Poppins',
                        }}
                      >
                        {reason?.explanationText ?? ''}
                      </p>
                    )}
                    {reason?.displayQrCode && liveQrUrl && (
                      <div className="flex items-center justify-center">
                        <QRCodeSVG value={liveQrUrl} size={248} className="" />
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            {quitPage?.otherReason && (
              <div className="flex flex-col gap-4">
                <div
                  className={`flex items-center gap-2 p-2 border border-input rounded-lg cursor-pointer hover:bg-gray-50 ${
                    otherReasonSelected ? '' : ''
                  }`}
                  onClick={() => {
                    setOtherReasonSelected(!otherReasonSelected);
                  }}
                >
                  <Checkbox
                    id="checkbox-other-reason"
                    checked={otherReasonSelected}
                    onCheckedChange={() => setOtherReasonSelected(!otherReasonSelected)}
                    style={{
                      borderColor: visualIdentity?.secondaryBackgroundColor || '#E5E7EB',
                    }}
                  />
                  <label
                    htmlFor="checkbox-other-reason"
                    className="cursor-pointer flex-1"
                    style={{
                      color: textColor,
                      fontFamily: visualIdentity?.fontFamily || 'Poppins',
                    }}
                  >
                    {t('quitPopup.otherReason')}
                  </label>
                </div>
                {otherReasonSelected && (
                  <div>
                    <textarea
                      placeholder={t('quitPopup.otherReasonPlaceholder')}
                      value={otherReasonText}
                      onChange={(e) => setOtherReasonText(e.target.value)}
                      className="w-full p-3 border border-input rounded-lg resize-none"
                      rows={3}
                      style={{
                        color: textColor,
                        fontFamily: visualIdentity?.fontFamily || 'Poppins',
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex items-center gap-4 w-full">
        <div className="w-1/2">
          <Button
            variant="outline"
            className="w-full"
            onClick={onCancel}
            style={{
              borderColor: buttonColor,
              color: buttonColor,
              fontFamily: visualIdentity?.fontFamily || 'Poppins',
            }}
          >
            {t('quitPopup.previewButtons.cancel')}
          </Button>
        </div>
        <div className="w-1/2">
          <Button
            variant="default"
            className="w-full"
            disabled={!canSubmit}
            onClick={handleSubmit}
            style={{
              // Fond accent seulement quand actif : sinon l'inline écrasait le style désactivé
              // (bg + texte) du Button et donnait un bouton accent délavé au texte illisible.
              backgroundColor: canSubmit ? buttonColor : undefined,
              color: canSubmit ? buttonTextColor : undefined,
              fontFamily: visualIdentity?.fontFamily || 'Poppins',
            }}
          >
            {t('quitPopup.previewButtons.saveAndQuit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
